'use client'
import React, { useEffect } from 'react'
import { Box, Rating, Card, Stack, Typography, styled } from '@mui/material'
import { useTranslations } from 'next-intl'
import StarIcon from '@mui/icons-material/Star'
import { useReviewsSumary } from '../../hooks/use-review-sumary'

interface IReviewStarProps {
  productId: number
  selectedRating?: number
  onRatingChange?: (rating?: number) => void
  isLoading?: boolean
  totalReviews?: number
}

export default function SidebarReviewFilters({
  productId,
  selectedRating,
  onRatingChange,
  isLoading: isFilterLoading,
  totalReviews,
}: IReviewStarProps) {
  const { reviewSummary, isLoading, error } = useReviewsSumary(productId)
  const t = useTranslations()
  const StyledCard = styled(Card)(({ theme }) => ({
    // border: `1px solid ${theme.palette.mode === 'dark' ? '#333' : '#bbbbba'}`,
    borderRadius: '24px',
    padding: '24px',
    boxShadow: 'none',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    [theme.breakpoints.down('sm')]: {
      gap: '8px',
      padding: '12px',
      borderRadius: '12px',
    },
  }))
  const [disable, setDisableFilter] = React.useState(false)
  useEffect(() => {
    if (totalReviews === 0) {
      setDisableFilter(true)
    } else {
      setDisableFilter(false)
    }
  }, [totalReviews])
  if (isLoading) {
    return (
      <StyledCard>
        <Stack spacing={2}>
          <Box sx={{ width: '60%', height: 24, bgcolor: '#f0f0f0', borderRadius: 1 }} />
          <Box>
            <Box sx={{ width: '40%', height: 16, bgcolor: '#f0f0f0', borderRadius: 1, mb: 1.5 }} />
            <Stack spacing={1}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box
                  key={i}
                  sx={{
                    height: 48,
                    bgcolor: '#f0f0f0',
                    borderRadius: '12px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </StyledCard>
    )
  }

  const { avg_rating, star_1, star_2, star_3, star_4, star_5 } = reviewSummary || {
    avg_rating: 0,
    total_reviews: 0,
    star_1: 0,
    star_2: 0,
    star_3: 0,
    star_4: 0,
    star_5: 0,
  }

  const ratingBreakdown = {
    5: star_5,
    4: star_4,
    3: star_3,
    2: star_2,
    1: star_1,
  }
  const handleFilterClick = (rating?: number) => {
    if (onRatingChange && !disable) {
      onRatingChange(rating)
      setTimeout(() => {
        const reviewSection = document.getElementById('review')
        if (reviewSection) {
          const yOffset = -100
          const y = reviewSection.getBoundingClientRect().top + window.scrollY + yOffset
          window.scrollTo({ top: y, behavior: 'smooth' })
        }
      }, 100)
    }
  }
  return (
    <StyledCard>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight={600}>
          {t('LỌC ĐÁNH GIÁ')}
        </Typography>
        {selectedRating && (
          <Typography
            variant="body2"
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={() => !isFilterLoading && handleFilterClick(undefined)}
          >
            {t('Bỏ lọc')}
          </Typography>
        )}
      </Stack>

      <Box>
        <Typography variant="body2" fontWeight={600} color="#3a3a3a" mb={1.5}>
          {t('THEO SỐ SAO')}
        </Typography>
        <Stack
          gap={1}
          flexWrap={'wrap'}
          sx={{
            flexDirection: {
              xs: 'row',
              sm: 'column',
            },
            pointerEvents: disable ? 'none' : 'auto',
            opacity: disable ? 0.6 : 1,
          }}
        >
          {[5, 4, 3, 2, 1].map((star) => (
            <Stack
              key={star}
              direction="row"
              alignItems="center"
              onClick={() => !isFilterLoading && handleFilterClick(star)}
              sx={{
                cursor: isFilterLoading ? 'not-allowed' : 'pointer',
                p: { xs: '6px', sm: '10px 16px' },
                borderRadius: { xs: '4px', sm: '8px' },
                border: '1px solid #E0E0E0',
                transition: 'all 0.2s ease',
                borderColor: selectedRating === star ? 'primary.main' : '#E0E0E0',
                opacity: isFilterLoading ? 0.6 : 1,
                pointerEvents: isFilterLoading ? 'none' : 'auto',

                '&:hover': {
                  bgcolor: isFilterLoading ? 'transparent' : '#f8f8f8',
                  borderColor: 'primary.main',
                },
              }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                flex={1}
                sx={{
                  display: {
                    xs: 'none',
                    sm: 'flex',
                  },
                }}
              >
                {Array.from({ length: star }).map((_, i) => (
                  <StarIcon key={i} sx={{ fontSize: 18, color: '#FEB50D' }} />
                ))}
                {Array.from({ length: 5 - star }).map((_, i) => (
                  <StarIcon key={i} sx={{ fontSize: 18, color: '#E0E0E0' }} />
                ))}
              </Stack>
              <Typography
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                }}
                variant="body2"
                fontWeight={500}
                color="#666"
              >
                <Typography
                  component={'span'}
                  sx={{
                    display: {
                      xs: 'flex',
                      lg: 'none',
                    },
                    alignItems: 'center',
                  }}
                >
                  {star} <Rating value={1} max={1} readOnly size="small" sx={{ fontSize: '1rem' }} />
                </Typography>
                ({ratingBreakdown[star as keyof typeof ratingBreakdown].toLocaleString('vi-VN')})
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </StyledCard>
  )
}
